/**
 * Monthly "Account work" summaries — one row per client × calendar month. The latest
 * summary marked `include_in_digest` is rendered in the monthly PDF + email so the
 * client gets a recap of what was actually done alongside the metrics.
 */
import React, { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ListChecks, Pencil, Check, X } from "lucide-react";

/** Returns "YYYY-MM" for the current month. */
function currentPeriod() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** Returns "YYYY-MM" for the previous calendar month. */
function previousPeriod() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** "2026-02" → "February 2026" */
function prettyPeriod(period) {
  if (!period || period.length !== 7) return period || "—";
  const [y, m] = period.split("-");
  return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
}

/** Generate the last N "YYYY-MM" labels, newest first. */
function recentPeriodOptions(n = 12) {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < n; i++) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

export default function WorkSummariesCard({ clientId }) {
  const [summaries, setSummaries] = useState([]);
  const [period, setPeriod] = useState(previousPeriod()); // default to last month (the one the next digest will cover)
  const [body, setBody] = useState("");
  const [includeInDigest, setIncludeInDigest] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // {id, body, include_in_digest}

  const load = useCallback(async () => {
    const { data } = await api.get(`/work-summaries/${clientId}`);
    setSummaries(data);
    // One-shot pre-fill of the form on initial load only — if a summary already exists for
    // the default selected period, populate the textarea so the next save updates it.
    const existing = (data || []).find((s) => s.period === period);
    if (existing) {
      setBody(existing.body || "");
      setIncludeInDigest(!!existing.include_in_digest);
    }
  }, [clientId, period]);
  useEffect(() => { load(); }, [load]);

  /** Switching month resets the form (and pre-fills if that month already has a summary). */
  const switchPeriod = useCallback((newPeriod) => {
    setPeriod(newPeriod);
    const existing = summaries.find((s) => s.period === newPeriod);
    setBody(existing?.body || "");
    setIncludeInDigest(existing?.include_in_digest ?? true);
  }, [summaries]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!body.trim()) {
      toast.error("Add some content first.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/work-summaries", {
        client_id: clientId,
        period,
        body: body.trim(),
        include_in_digest: includeInDigest,
      });
      setSummaries((prev) => {
        const existingIdx = prev.findIndex((p) => p.period === period);
        if (existingIdx >= 0) {
          const copy = [...prev];
          copy[existingIdx] = data;
          return copy;
        }
        return [data, ...prev].sort((a, b) => b.period.localeCompare(a.period));
      });
      toast.success(`Saved summary for ${prettyPeriod(period)}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this work summary?")) return;
    await api.delete(`/work-summaries/${id}`);
    setSummaries((prev) => prev.filter((s) => s.id !== id));
  };

  const startEditing = (s) => setEditing({ id: s.id, body: s.body, include_in_digest: s.include_in_digest });
  const cancelEditing = () => setEditing(null);
  const saveEditing = async () => {
    try {
      const { data } = await api.patch(`/work-summaries/${editing.id}`, {
        body: editing.body,
        include_in_digest: editing.include_in_digest,
      });
      setSummaries((prev) => prev.map((s) => (s.id === data.id ? data : s)));
      toast.success("Updated.");
      setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    }
  };

  const toggleInclude = async (s) => {
    try {
      const { data } = await api.patch(`/work-summaries/${s.id}`, { include_in_digest: !s.include_in_digest });
      setSummaries((prev) => prev.map((p) => (p.id === data.id ? data : p)));
    } catch (err) {
      toast.error("Failed to toggle digest inclusion");
    }
  };

  const inputCls = "w-full bg-white border border-[#e2e8f0] rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all";
  const monthOptions = recentPeriodOptions(12);
  const nextDigestPeriod = previousPeriod();
  const existingForCurrent = summaries.find((s) => s.period === period);

  return (
    <div className="space-y-4" data-testid="work-summaries-card">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold inline-flex items-center gap-1.5">
            <ListChecks className="h-3 w-3" /> Account work
          </div>
          <h3 className="font-display text-xl mt-0.5 text-[#1a202c]">Monthly summaries</h3>
        </div>
        <div className="text-[10px] text-[#a0aec0] font-mono-tight uppercase tracking-widest text-right">
          Next digest will include: <span className="text-[#234e52] font-bold">{prettyPeriod(nextDigestPeriod)}</span>
        </div>
      </div>

      {/* New / edit form */}
      <form onSubmit={submit} className="card-soft p-5 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">Month</label>
          <select
            value={period}
            onChange={(e) => switchPeriod(e.target.value)}
            data-testid="work-summary-period"
            className="bg-white border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all"
          >
            {monthOptions.map((p) => (
              <option key={p} value={p}>
                {prettyPeriod(p)} {p === currentPeriod() ? "· current" : ""} {p === previousPeriod() ? "· next digest" : ""}
              </option>
            ))}
          </select>
          {existingForCurrent && (
            <span className="text-[10px] uppercase tracking-widest font-bold text-[#234e52] bg-[#4fd1c5]/15 border border-[#4fd1c5]/40 px-2 py-1 rounded-md">
              Editing existing
            </span>
          )}
        </div>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={"e.g.\n- Published 3 new blog posts targeting 'audi specialist stockport'\n- Added 12 location-specific landing pages\n- Fixed Core Web Vitals on the homepage\n- Built 8 new local citations"}
          rows={6}
          data-testid="work-summary-body"
          className={`${inputCls} resize-y font-mono-tight text-[13px]`}
        />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 text-xs text-[#4a5568] cursor-pointer">
            <input
              type="checkbox"
              checked={includeInDigest}
              onChange={(e) => setIncludeInDigest(e.target.checked)}
              data-testid="work-summary-include"
              className="accent-[#4fd1c5] h-4 w-4"
            />
            Include in monthly digest email + PDF report
          </label>
          <button
            type="submit"
            disabled={saving || !body.trim()}
            data-testid="work-summary-save"
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" /> {existingForCurrent ? "Save changes" : "Add summary"}
          </button>
        </div>
        <div className="text-[10px] text-[#a0aec0]">
          Use one line per item — they&apos;ll render as bullets in the PDF + email. Each client × month has one summary; saving the same month overwrites it.
        </div>
      </form>

      {/* History */}
      <div className="space-y-2.5">
        {summaries.length === 0 && (
          <div className="text-sm text-[#a0aec0] text-center py-6 card-soft">
            No work summaries yet.
          </div>
        )}
        {summaries.map((s) => {
          const isEditing = editing?.id === s.id;
          return (
            <div key={s.id} data-testid={`work-summary-${s.id}`} className="card-soft p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#234e52] bg-[#4fd1c5]/10 px-2 py-1 rounded-md">
                    {prettyPeriod(s.period)}
                  </div>
                  <button
                    onClick={() => toggleInclude(s)}
                    data-testid={`work-summary-toggle-${s.id}`}
                    title={s.include_in_digest ? "Currently included in digest — click to exclude" : "Currently excluded — click to include"}
                    className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-md border ${
                      s.include_in_digest
                        ? "text-[#048056] bg-[#4fd1c5]/10 border-[#4fd1c5]/40"
                        : "text-[#a0aec0] border-[#e2e8f0]"
                    }`}
                  >
                    {s.include_in_digest ? "In digest" : "Excluded"}
                  </button>
                </div>
                <div className="flex items-center gap-1">
                  {!isEditing && (
                    <button
                      onClick={() => startEditing(s)}
                      data-testid={`work-summary-edit-${s.id}`}
                      className="text-[#a0aec0] hover:text-[#2c7a7b] p-1"
                      title="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => remove(s.id)}
                    data-testid={`work-summary-delete-${s.id}`}
                    className="text-[#a0aec0] hover:text-[#f56565] p-1"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              {isEditing ? (
                <div className="mt-3 space-y-2">
                  <textarea
                    value={editing.body}
                    onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                    rows={6}
                    className={`${inputCls} resize-y font-mono-tight text-[13px]`}
                    data-testid={`work-summary-edit-body-${s.id}`}
                  />
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={cancelEditing}
                      className="text-xs text-[#718096] inline-flex items-center gap-1 px-2 py-1"
                    >
                      <X className="h-3 w-3" /> Cancel
                    </button>
                    <button
                      onClick={saveEditing}
                      data-testid={`work-summary-edit-save-${s.id}`}
                      className="btn-primary text-xs inline-flex items-center gap-1"
                    >
                      <Check className="h-3 w-3" /> Save
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-[#4a5568] mt-3 whitespace-pre-wrap font-mono-tight text-[13px]">
                  {s.body}
                </div>
              )}
              <div className="text-[10px] text-[#a0aec0] font-mono-tight mt-3">
                Updated {new Date(s.updated_at || s.created_at).toLocaleString()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
